/**
 * Implementation extended from:
 * https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 */

/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import type { Document, Text } from '@contentful/rich-text-types';
import {
  SharedArticleVideoEmbedder,
  SharedArticlesRichText,
  getDefaultArticlesRichTextRenderNodes,
} from '@coursera-web/shared-articles';
import { kebabCase } from 'lodash';

import type { EnterpriseArticleLinkFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import EnterpriseArticleHighlight from 'bundles/ent-website/articles/components/EnterpriseArticleHighlight';
import EnterpriseArticleInlinePromoUnit from 'bundles/ent-website/articles/components/EnterpriseArticleInlinePromoUnit';
import EnterpriseArticleNativeAd from 'bundles/ent-website/articles/components/EnterpriseArticleNativeAd';
import EnterpriseArticlePromoUnit from 'bundles/ent-website/articles/components/EnterpriseArticlePromoUnit';
import { enterpriseArticleRichTextEmbedTypes } from 'bundles/ent-website/articles/constants';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

type OwnProps = {
  content: Document;
  links?: EnterpriseArticleLinkFragment;
  getRichTextOptions?: (module: BlockLevelModule, links?: EnterpriseArticleLinkFragment) => Options;
  module: BlockLevelModule;
};

type Props = OwnProps & Omit<React.HTMLProps<HTMLDivElement>, keyof OwnProps>;

export const getDefaultRichTextOptions = (module: BlockLevelModule, links?: EnterpriseArticleLinkFragment): Options => {
  let headingIdx = -1;
  return {
    /**
     * This method is ran, in order, for every node in the rich text body. It can be used to
     * specify how documentToReactComponents should render specific nodes.
     * When it is run, we know the location of the current node, but nothing about the node
     * itself. To counter this we use the id attribute to find the asset, entry, etc corresponding
     * to the current node, then render accordingly.
     */
    renderNode: {
      ...getDefaultArticlesRichTextRenderNodes(links),
      [BLOCKS.HEADING_2]: (node, children) => {
        const textBlock = node?.content?.[0] as Text;
        const kebabCaseTextContent = kebabCase(textBlock?.value);
        headingIdx += 1;
        return <h2 id={`${headingIdx}-${kebabCaseTextContent}`}>{children}</h2>;
      },
      /**
       * Renders embedded entries.
       */
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const id = node?.data?.target?.sys?.id;
        const entry = links?.entries?.block?.find((link) => link?.sys?.id === id);

        switch (entry?.__typename) {
          case enterpriseArticleRichTextEmbedTypes.articleNativeAd:
            return (
              <EnterpriseArticleNativeAd
                alignment={entry?.alignment}
                headline={entry?.headline}
                body={entry?.promoBody}
                cta={entry?.cta}
                image={entry?.image}
                module={module}
              />
            );
          case enterpriseArticleRichTextEmbedTypes.articleInlinePromoUnit:
            return (
              <EnterpriseArticleInlinePromoUnit
                type={entry?.alignment}
                headline={entry?.headline}
                body={entry?.promoBody}
                cta={entry?.cta}
                logo={entry?.logo}
                image={entry?.image}
                backgroundColor={entry?.backgroundColor}
                fontColor={entry?.fontColor}
                hideImageOnMobile={entry?.hideImageOnMobile}
                module={module}
              />
            );

          case enterpriseArticleRichTextEmbedTypes.articlePromoUnit:
            return (
              <EnterpriseArticlePromoUnit
                headline={entry?.headline}
                body={entry?.promoBody}
                cta={entry?.cta}
                logo={entry?.logo}
                coverImage={entry?.coverImage}
                rightContentType={entry?.rightContentType}
                image={entry?.image}
                partnerList={entry?.partnerList}
                bulletPoints={entry?.bulletPointsCollection?.items}
                leftBackgroundColor={entry?.leftBackgroundColor}
                rightBackgroundColor={entry?.rightBackgroundColor}
                leftFontColor={entry?.leftFontColor}
                rightFontColor={entry?.rightFontColor}
                module={module}
              />
            );
          case enterpriseArticleRichTextEmbedTypes.articleVideo:
            return (
              <SharedArticleVideoEmbedder
                title={entry?.title}
                externalEmbedVideoUrl={entry?.iFrameEmbed}
                video={entry?.video}
                previewImage={entry?.previewImage}
              />
            );
          case enterpriseArticleRichTextEmbedTypes.articleHighlight:
            if (!entry?.body) return null;

            return <EnterpriseArticleHighlight body={entry.body} module={module} />;
          default:
            return null;
        }
      },
    },
  };
};

const EnterpriseArticleRichText = ({
  content,
  links,
  getRichTextOptions = getDefaultRichTextOptions,
  module,
  ...divElementProps
}: Props) => {
  return <SharedArticlesRichText content={content} options={getRichTextOptions(module, links)} {...divElementProps} />;
};

export default EnterpriseArticleRichText;
